import { useWizard } from 'react-use-wizard';
import { motion } from 'framer-motion';
import { AnimatedStep } from '../AnimatedStep';
import type { SetupData } from '../SetupWizard';

interface Props {
  data: SetupData;
  onComplete: () => void;
  isLoading: boolean;
  error?: string;
}

const DAY_NAMES: Record<string, string> = {
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
  SUN: 'Sunday',
};

export function ConfirmationStep({ data, onComplete, isLoading, error }: Props) {
  const { previousStep } = useWizard();

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const workDaysFormatted = data.work_days
    .map((d) => DAY_NAMES[d])
    .join(', ');

  return (
    <AnimatedStep>
      <div className="space-y-6">
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center"
          >
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </motion.div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            You're all set, {data.name}!
          </h2>
          <p className="text-gray-600">
            Here's a summary of your settings.
          </p>
        </div>

        <div className="bg-gray-50 rounded-xl p-5 space-y-4">
          <div className="flex justify-between">
            <span className="text-gray-600">Home Base</span>
            <span className="font-medium text-gray-900 text-right max-w-[200px] truncate">
              {data.home_address}
            </span>
          </div>
          <div className="border-t border-gray-200" />
          <div className="flex justify-between">
            <span className="text-gray-600">Work Days</span>
            <span className="font-medium text-gray-900 text-right max-w-[200px]">
              {workDaysFormatted}
            </span>
          </div>
          <div className="border-t border-gray-200" />
          <div className="flex justify-between">
            <span className="text-gray-600">Hours</span>
            <span className="font-medium text-gray-900">
              {formatTime(data.work_start_time)} – {formatTime(data.work_end_time)}
            </span>
          </div>
        </div>

        <p className="text-sm text-gray-500 text-center">
          You can change these anytime in Settings.
        </p>

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-xl text-sm text-center">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => previousStep()}
            disabled={isLoading}
            className="px-6 py-3 text-gray-600 font-medium hover:text-gray-900 transition-colors disabled:opacity-50"
          >
            Back
          </button>
          <button
            onClick={onComplete}
            disabled={isLoading}
            className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Saving...
              </>
            ) : (
              'Start Finding Stores'
            )}
          </button>
        </div>
      </div>
    </AnimatedStep>
  );
}
